import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Quote, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { JournalFormattedView } from '@/components/journal/JournalFormattedView';
import type { JournalEntryWithAuthor } from '@/hooks/useJournal';
import { formatCourseDateTime } from '@/lib/courseDateFormat';
import { journalHtmlToPlainText } from '@/lib/journal/journalRichText';
import { journalQuoteHtml, selectedTextInNode } from '@/lib/journalQuote';

const ICON = 1.75;

export function JournalWriteReadingPanel({
  entry,
  onCite,
  onClose,
}: {
  entry: JournalEntryWithAuthor;
  onCite: (html: string) => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const when = formatCourseDateTime(entry.encounter_at ?? entry.entry_date, i18n.language);
  const clinician = entry.author_name || t('journal.untitled');
  const title = entry.title || t('journal.untitled');

  useEffect(() => {
    panelRef.current?.focus();
  }, [entry.id]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function cite() {
    const selected = selectedTextInNode(bodyRef.current);
    const text = selected || entry.content_md.trim() || journalHtmlToPlainText(entry.content_html);
    if (text) {
      onCite(
        journalQuoteHtml({
          text,
          attribution: t('journal.quote_attribution', { date: when, name: clinician }),
        }),
      );
    }
    onClose();
  }

  return createPortal(
    <div
      ref={panelRef}
      className="jw-read"
      role="dialog"
      aria-modal="true"
      aria-labelledby="jw-read-title"
      tabIndex={-1}
    >
      <div className="jw-read-h">
        <div className="jw-read-id">
          <h2 id="jw-read-title">{title}</h2>
          <p>
            {when}
            {` · ${clinician}`}
          </p>
        </div>
        <div className="jw-read-act">
          <Button type="button" variant="secondary" onClick={cite}>
            <Quote className="h-4 w-4" strokeWidth={ICON} aria-hidden />
            {t('journal.cite')}
          </Button>
          <Button type="button" variant="tertiary" onClick={onClose}>
            <X className="h-4 w-4" strokeWidth={ICON} aria-hidden />
            {t('journal.write_close')}
          </Button>
        </div>
      </div>
      <div className="jw-read-body jw-scroller" ref={bodyRef}>
        <div className="jw-read-tx">
          <JournalFormattedView html={entry.content_html} text={entry.content_md} />
        </div>
      </div>
    </div>,
    document.body,
  );
}
